import React, { useEffect, useMemo, useState } from 'react'
import {
  AlertTriangle, Archive, Building2, Copy, Pencil, Plus, Save,
  Shield, Trash2, Users, X
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { apiClient } from '../config/api'
import { useAuth } from '../contexts/AuthContext'
import type { AccountType } from '../services/authService'

interface SimpleRoleManagementProps {
  showMessage?: (message: string, type?: 'success' | 'error' | 'warning' | 'info') => void
}

interface Capability {
  key: string
  description?: string
  category: string
  accountTypes: AccountType[]
}

interface RoleTemplate {
  id: number
  name: string
  displayName: string
  description?: string | null
  family: AccountType
  isSystem: boolean
  isActive: boolean
  assignmentCount: number
  capabilities: string[]
}

interface RoleDraft {
  id?: number
  sourceId?: number
  name: string
  displayName: string
  description: string
  family: AccountType
  capabilities: string[]
  isSystem?: boolean
}

const dangerousCapabilities = new Set([
  'users.manage', 'customer.users.manage', 'settings.manage', 'mail.manage', 'customers.manage'
])

const blankRole = (family: AccountType): RoleDraft => ({
  name: '',
  displayName: '',
  description: '',
  family,
  capabilities: []
})

const SimpleRoleManagement: React.FC<SimpleRoleManagementProps> = ({ showMessage }) => {
  const { t } = useTranslation()
  const { isPlatformAdmin } = useAuth()
  const [family, setFamily] = useState<AccountType>('CUSTOMER')
  const [roles, setRoles] = useState<RoleTemplate[]>([])
  const [catalog, setCatalog] = useState<Capability[]>([])
  const [selectedRoleId, setSelectedRoleId] = useState<number | null>(null)
  const [draft, setDraft] = useState<RoleDraft | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  const load = async () => {
    setLoading(true)
    try {
      const response = await apiClient.get('/access/catalog')
      const data = response.data || {}
      setRoles((data.roleTemplates || []).map((role: any) => ({
        id: Number(role.id),
        name: String(role.name),
        displayName: String(role.displayName ?? role.display_name ?? role.name),
        description: role.description,
        family: String(role.family).toUpperCase() as AccountType,
        isSystem: Boolean(role.isSystem ?? role.is_system),
        isActive: Boolean(role.isActive ?? role.is_active),
        assignmentCount: Number(role.assignmentCount ?? role.assignment_count ?? 0),
        capabilities: Array.isArray(role.capabilities) ? role.capabilities.map(String) : []
      })))
      setCatalog((data.capabilities || []).map((item: any) => ({
        key: String(item.key ?? item.capability_key ?? item),
        description: item.description,
        category: String(item.category || 'General'),
        accountTypes: (item.accountTypes ?? item.account_types ?? []).map(
          (accountType: unknown) => String(accountType).toUpperCase() as AccountType
        )
      })))
    } catch (error: any) {
      showMessage?.(error?.message || t('access.roles.loadFailed'), 'error')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load() }, [isPlatformAdmin])

  const selectedRole = roles.find(role => role.id === selectedRoleId) || null
  const visibleRoles = roles.filter(role => role.family === family)
  const activeFamily = draft?.family ?? selectedRole?.family ?? family
  const groupedCatalog = useMemo(() => catalog
    .filter(capability => capability.accountTypes.includes(activeFamily))
    .reduce<Record<string, Capability[]>>((groups, capability) => {
      ;(groups[capability.category] ||= []).push(capability)
      return groups
    }, {}), [activeFamily, catalog])

  const openEdit = (role: RoleTemplate) => setDraft({
    id: role.id,
    name: role.name,
    displayName: role.displayName,
    description: role.description || '',
    family: role.family,
    capabilities: [...role.capabilities],
    isSystem: role.isSystem
  })

  const openClone = (role: RoleTemplate) => setDraft({
    sourceId: role.id,
    name: `${role.name}_COPY`,
    displayName: `${role.displayName} ${t('access.roles.copySuffix')}`,
    description: role.description || '',
    family: role.family,
    capabilities: [...role.capabilities]
  })

  const saveRole = async () => {
    if (!draft?.name.trim() || !draft.displayName.trim()) {
      return showMessage?.(t('access.roles.required'), 'warning')
    }
    setSaving(true)
    try {
      const payload = {
        name: draft.name,
        displayName: draft.displayName,
        description: draft.description,
        family: draft.family,
        capabilities: draft.capabilities
      }
      if (draft.id) await apiClient.put(`/roles/${draft.id}`, payload)
      else if (draft.sourceId) await apiClient.post(`/roles/${draft.sourceId}/clone`, payload)
      else await apiClient.post('/roles', payload)
      showMessage?.(t('access.roles.saved'), 'success')
      setDraft(null)
      await load()
    } catch (error: any) {
      showMessage?.(error?.message || t('access.roles.saveFailed'), 'error')
    } finally {
      setSaving(false)
    }
  }

  const archiveRole = async (role: RoleTemplate) => {
    if (!window.confirm(t('access.roles.archiveConfirm', { name: role.displayName }))) return
    try {
      await apiClient.post(`/roles/${role.id}/archive`)
      showMessage?.(t('access.roles.archived'), 'success')
      setSelectedRoleId(null)
      await load()
    } catch (error: any) {
      showMessage?.(error?.message || t('access.roles.saveFailed'), 'error')
    }
  }

  const deleteRole = async (role: RoleTemplate) => {
    if (!window.confirm(t('access.roles.deleteConfirm', { name: role.displayName }))) return
    try {
      await apiClient.delete(`/roles/${role.id}`)
      showMessage?.(t('access.roles.deleted'), 'success')
      setSelectedRoleId(null)
      await load()
    } catch (error: any) {
      showMessage?.(error?.message || t('access.roles.saveFailed'), 'error')
    }
  }

  const toggleCapability = (key: string) => setDraft(current => current ? ({
    ...current,
    capabilities: current.capabilities.includes(key)
      ? current.capabilities.filter(item => item !== key)
      : [...current.capabilities, key]
  }) : current)

  return (
    <div className="flex h-full min-h-0 flex-col bg-slate-50">
      <div className="border-b border-slate-200 bg-white p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex gap-3">
            <Shield className="text-indigo-600" />
            <div>
              <h2 className="font-semibold text-slate-900">{t('access.roles.title')}</h2>
              <p className="text-xs text-slate-500">
                {isPlatformAdmin ? t('access.roles.platformAdminHint') : t('access.roles.tenantAdminHint')}
              </p>
            </div>
          </div>
          {isPlatformAdmin && (
            <button onClick={() => setDraft(blankRole(family))}
              className="flex items-center gap-2 rounded-lg bg-indigo-600 px-3 py-2 text-sm font-medium text-white">
              <Plus size={15} />{t('access.roles.create')}
            </button>
          )}
        </div>
        <div className="mt-4 flex gap-2">
          {(['CUSTOMER', 'FLEET_TEAM'] as AccountType[]).map(item => (
            <button key={item} onClick={() => { setFamily(item); setSelectedRoleId(null) }}
              disabled={!isPlatformAdmin && item === 'FLEET_TEAM'}
              className={`flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-medium ${
                family === item ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-100 text-slate-600'
              } disabled:hidden`}>
              {item === 'CUSTOMER' ? <Building2 size={14} /> : <Users size={14} />}
              {t(item === 'CUSTOMER' ? 'access.customerUsers' : 'access.fleetTeam')}
            </button>
          ))}
        </div>
      </div>
      <div className="grid min-h-0 flex-1 grid-cols-1 overflow-hidden lg:grid-cols-[minmax(300px,0.9fr)_minmax(360px,1.1fr)]">
        <div className="overflow-y-auto border-r border-slate-200 p-4">
          {loading ? <p className="p-8 text-center text-sm text-slate-400">{t('loading')}</p> :
            <div className="space-y-3">
              {visibleRoles.map(role => (
                <button key={role.id} onClick={() => setSelectedRoleId(role.id)}
                  className={`w-full rounded-xl border p-4 text-left transition ${
                    selectedRoleId === role.id ? 'border-indigo-300 bg-indigo-50' : 'border-slate-200 bg-white hover:border-slate-300'
                  } ${!role.isActive ? 'opacity-65' : ''}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <span className="font-semibold text-slate-900">{role.displayName}</span>
                        <span className={`rounded-full px-2 py-0.5 text-[10px] ${
                          role.isSystem ? 'bg-violet-100 text-violet-700' : 'bg-cyan-100 text-cyan-700'
                        }`}>{t(role.isSystem ? 'access.roles.system' : 'access.roles.custom')}</span>
                        {!role.isActive && <span className="rounded-full bg-slate-200 px-2 py-0.5 text-[10px] text-slate-600">{t('access.roles.archivedLabel')}</span>}
                      </div>
                      <p className="mt-1 line-clamp-2 text-xs text-slate-500">{role.description || t('access.roles.noDescription')}</p>
                    </div>
                    <span className="whitespace-nowrap rounded bg-slate-100 px-2 py-1 text-[10px] text-slate-600">
                      {t('access.roles.assignments', { count: role.assignmentCount })}
                    </span>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-1">
                    {[...new Set(role.capabilities.map(key => catalog.find(item => item.key === key)?.category || key.split('.')[0]))]
                      .map(category => <span key={category} className="rounded bg-white px-2 py-1 text-[10px] text-slate-500">{category}</span>)}
                  </div>
                </button>
              ))}
              {!visibleRoles.length && <p className="p-8 text-center text-sm text-slate-400">{t('access.roles.none')}</p>}
            </div>}
        </div>
        <div className="overflow-y-auto p-4">
          {!selectedRole ? (
            <div className="flex h-full items-center justify-center text-center text-sm text-slate-400">{t('access.roles.select')}</div>
          ) : (
            <div className="space-y-4">
              <div className="rounded-xl border border-slate-200 bg-white p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900">{selectedRole.displayName}</h3>
                    <div className="mt-1 font-mono text-xs text-slate-400">{selectedRole.name}</div>
                  </div>
                  {isPlatformAdmin && <div className="flex gap-1">
                    <button onClick={() => openClone(selectedRole)} title={t('access.roles.clone')} className="rounded-lg p-2 text-slate-500 hover:bg-slate-100"><Copy size={15} /></button>
                    <button onClick={() => openEdit(selectedRole)} title={t('edit')} className="rounded-lg p-2 text-slate-500 hover:bg-slate-100"><Pencil size={15} /></button>
                    <button onClick={() => archiveRole(selectedRole)} disabled={selectedRole.isSystem || !selectedRole.isActive}
                      title={t('access.roles.archive')} className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 disabled:opacity-25"><Archive size={15} /></button>
                    <button onClick={() => deleteRole(selectedRole)} disabled={selectedRole.isSystem || selectedRole.assignmentCount > 0}
                      title={t('delete')} className="rounded-lg p-2 text-red-500 hover:bg-red-50 disabled:opacity-25"><Trash2 size={15} /></button>
                  </div>}
                </div>
                <p className="mt-3 text-sm text-slate-600">{selectedRole.description || t('access.roles.noDescription')}</p>
                {selectedRole.isSystem && <div className="mt-3 flex gap-2 rounded-lg bg-violet-50 p-3 text-xs text-violet-700">
                  <Shield size={15} className="shrink-0" />{t('access.roles.protectedHint')}
                </div>}
                {selectedRole.capabilities.some(key => dangerousCapabilities.has(key)) && (
                  <div className="mt-3 flex gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
                    <AlertTriangle size={15} className="shrink-0" />{t('access.roles.dangerousWarning')}
                  </div>
                )}
              </div>
              <div className="rounded-xl border border-slate-200 bg-white p-4">
                <h4 className="mb-3 text-sm font-semibold text-slate-800">{t('access.roles.effectivePreview')}</h4>
                <div className="space-y-4">
                  {Object.entries(groupedCatalog).map(([category, capabilities]) => {
                    const granted = capabilities.filter(capability => selectedRole.capabilities.includes(capability.key))
                    if (!granted.length) return null
                    return <div key={category}>
                      <div className="mb-2 text-[10px] font-bold uppercase tracking-wider text-slate-400">{category}</div>
                      <div className="grid gap-2 sm:grid-cols-2">{granted.map(capability => (
                        <div key={capability.key} className="rounded-lg bg-emerald-50 p-2 text-xs text-emerald-800">
                          <div className="font-medium">{capability.key}</div>
                          {capability.description && <div className="mt-0.5 text-[10px] text-emerald-700">{capability.description}</div>}
                        </div>
                      ))}</div>
                    </div>
                  })}
                  {!selectedRole.capabilities.length && <p className="text-xs text-slate-400">{t('access.roles.noCapabilities')}</p>}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      {draft && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center bg-slate-950/45 p-3" onMouseDown={() => setDraft(null)}>
          <div className="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
            onMouseDown={event => event.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-slate-200 p-4">
              <div><h3 className="font-semibold text-slate-900">{draft.id ? t('access.roles.edit') : draft.sourceId ? t('access.roles.clone') : t('access.roles.create')}</h3>
                <p className="text-xs text-slate-500">{t('access.roles.editorHint')}</p></div>
              <button onClick={() => setDraft(null)} className="p-1 text-slate-400"><X size={18} /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              <div className="grid gap-3 sm:grid-cols-2">
                <label className="text-xs font-medium text-slate-600">{t('access.roles.internalName')}
                  <input value={draft.name} disabled={draft.isSystem}
                    onChange={event => setDraft(current => current && ({ ...current, name: event.target.value.toUpperCase().replace(/\s+/g, '_') }))}
                    className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm disabled:bg-slate-100" />
                </label>
                <label className="text-xs font-medium text-slate-600">{t('access.roles.displayName')}
                  <input value={draft.displayName} onChange={event => setDraft(current => current && ({ ...current, displayName: event.target.value }))}
                    className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
                </label>
                <label className="text-xs font-medium text-slate-600 sm:col-span-2">{t('description')}
                  <textarea value={draft.description} onChange={event => setDraft(current => current && ({ ...current, description: event.target.value }))}
                    className="mt-1 min-h-20 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
                </label>
                <label className="text-xs font-medium text-slate-600">{t('access.roles.family')}
                  <select value={draft.family} disabled={draft.isSystem}
                    onChange={event => {
                      const nextFamily = event.target.value as AccountType
                      const available = new Set(catalog
                        .filter(capability => capability.accountTypes.includes(nextFamily))
                        .map(capability => capability.key))
                      setDraft(current => current && ({
                        ...current,
                        family: nextFamily,
                        capabilities: current.capabilities.filter(capability => available.has(capability))
                      }))
                    }}
                    className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm disabled:bg-slate-100">
                    <option value="CUSTOMER">{t('access.customerUsers')}</option>
                    <option value="FLEET_TEAM">{t('access.fleetTeam')}</option>
                  </select>
                </label>
              </div>
              <div className="mt-5 space-y-4">
                {Object.entries(groupedCatalog).map(([category, capabilities]) => (
                  <section key={category} className="rounded-xl border border-slate-200 p-3">
                    <div className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500">{category}</div>
                    <div className="grid gap-2 sm:grid-cols-2">{capabilities.map(capability => (
                      <label key={capability.key} className={`flex items-start gap-2 rounded-lg p-2 text-xs ${
                        dangerousCapabilities.has(capability.key) ? 'bg-amber-50' : 'bg-slate-50'
                      }`}>
                        <input type="checkbox" checked={draft.capabilities.includes(capability.key)}
                          onChange={() => toggleCapability(capability.key)} className="mt-0.5 rounded border-slate-300 text-indigo-600" />
                        <span><span className="font-medium text-slate-700">{capability.key}</span>
                          {capability.description && <span className="mt-0.5 block text-[10px] text-slate-500">{capability.description}</span>}</span>
                      </label>
                    ))}</div>
                  </section>
                ))}
              </div>
            </div>
            <div className="flex justify-end gap-2 border-t border-slate-200 p-4">
              <button onClick={() => setDraft(null)} className="rounded-lg bg-slate-100 px-4 py-2 text-sm text-slate-700">{t('cancel')}</button>
              <button onClick={saveRole} disabled={saving} className="flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm text-white disabled:opacity-50">
                <Save size={14} />{saving ? '…' : t('save')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default SimpleRoleManagement
